/* eslint-disable camelcase */
import React, { useContext, useMemo } from 'react';
import { analyticService } from 'global/services';

/* UTILS AND CONTEXT */
import { LOADER_APPEARING_TIME, loaderDispatchContext } from 'app/containers/contexts/loader.context';
import { isStandaloneApp } from 'app/utils/browser';

import {
    ADD_ITEMS_TO_CART_BY_SCHEMA,
} from 'app/graphql/network/basket/basketMutations';

/* HOOKS AND COMPOENTS */
import { menuDatesState } from 'app/containers/contexts/menuDates.context';
import { gql, useMutation, useReactiveVar } from '@apollo/client';
import { parseMenuType, parseTags } from 'app/utils/basketSchema';
import { deleteTagReaction } from 'app/hooks/useFilterHandlers';
import {
    selectedPeriodVar, subscriptionTypeVar,
} from 'app/apollo/reaction';
import { onCompletedBillingMutation } from 'app/containers/BasketResolver';
import { useBillingMutationVars } from 'app/hooks/accessors/useBillingMutationVars';
import { useHistory } from 'react-router';
import { URLUpdater } from 'global/utils';
import { useDisabledDatesSelected } from 'app/hooks';
import { RENDERING_CONTEXT_MENU, useDishDetails } from '../MobileDishDetails';
import {
    useBasketPreviewAside,
    useSliderCardsChain,
} from './hooks';
import { ISetsItem, TCardKey, cardLinkDataMap } from './sets.record';
import BestSetsMobileSliderV2 from './ABSets5Dishes/BestSetsMobileSliderV2';
import BR1258BestSetsMobileSliderV2 from './BR1258/BR1258ABSets5Dishes/BR1258BestSetsMobileSliderV2';

interface ISetsMobileSliderProps {
    isBasketEmpty: boolean,
    // eslint-disable-next-line no-unused-vars
    setTemporaryBasketPeriod: (I: boolean) => {},
    onSetPeriod: (startPeriod: string, value: true) => Promise<void>,
    openBasket: () => void,
}

const loaderTexts = {
    ru: { mainText: 'Заполняем корзину' },
    en: { mainText: 'Filling the cart' },
};

const additionalCardsChain: TCardKey[] = ['HALLOWEEN_SET'];


const SET_SLIDER_BILLING_MUTATION = gql`
    mutation SET_SLIDER_BILLING_MUTATION(
        $promocode: String,
        $period: String!,
        $payment_method: paymentMethod,
        $phone: String,
    ) {
        billing(
            promocode: $promocode,
            period: $period,
            payment_method: $payment_method,
            phone: $phone,
        ) {
            promocode
            payment_method
            isNewbie
        }
    }
`;


// ROOT
export const SetsMobileSlider = React.memo((props: ISetsMobileSliderProps) => {
    const { openDishDetails } = useDishDetails();
    const subscriptionType = useReactiveVar(subscriptionTypeVar);
    const billingVars = useBillingMutationVars();
    const period = useReactiveVar(selectedPeriodVar);
    const isDisabledDateSelected = useDisabledDatesSelected();

    const {
        isBasketEmpty = false,
        openBasket,
    } = props;

    const loaderDispatch = useContext(loaderDispatchContext);
    const cardsChain = useSliderCardsChain();
    const history = useHistory();

    const {
        state: {
            eeAvailableDates,
            selectedFilterPeriod,
            filterPeriod,
        },
    } = useContext(menuDatesState);

    /* DA */
    const [mutateBasketByScema] = useMutation(ADD_ITEMS_TO_CART_BY_SCHEMA);

    const [mutateBilling] = useMutation(SET_SLIDER_BILLING_MUTATION, {
        onCompleted: (data) => { onCompletedBillingMutation(data); },
    });

    /* HANDLERS */
    const handleFillBasketBy = async (cardKey: TCardKey, promocode = '') => {
        const data = cardLinkDataMap.get(cardKey);
        sessionStorage.setItem('gotBasketFromPreview', 'true');

        // ПОКАЗЫВАЕТ ЛОДЕР
        // @ts-ignore
        loaderDispatch({
            type: 'show',
            payload: { autoHide: true, ...loaderTexts.ru },
        });

        deleteTagReaction({ tagId: '1220', isUserChangesTags: true });

        const setPrams = new URLSearchParams(data?.href.replace('/basket/', ''));

        if (promocode) {
            setPrams.set('promo_code', promocode);
        }

        mutateBasketByScema({
            variables: {
                period,
                menuType: parseMenuType({ search: setPrams.toString() }),
                tags: parseTags({ search: setPrams.toString() }) || [],
                schema: [{ category_id: 3, count: 10 }],
            },
            onCompleted: (res) => {
                const dishes = res?.addItemsToCartBySchema?.cart?.sections
                    ?.map((section: any) => section?.items)
                    ?.flat(Infinity)
                    ?.map((dishData: any) => ({ ...dishData?.dish, portion: dishData?.portions }));
                const typeOfSet = data?.typeOfSet || res?.addItemsToCartBySchema?.cart?.typeOfSet;
                analyticService.push({
                    eventName: 'add_to_cart',
                    source: 'menu',
                    dishes,
                    subscriptionType,
                    typeOfSet,
                });
                // УХОДИМ В БАСКЕТ В БАСКЕТ
                setTimeout(() => {
                    analyticService.push({
                        // @ts-ignore
                        setName: data.trackId,
                        eventName: 'Track_Click_Best_Set',
                    });
                    // @ts-ignore
                    history.push(`/basket/?${setPrams.toString()}`);
                }, LOADER_APPEARING_TIME);
            },
            context: {
                message: 'app:mutate:SetsMobileSlider',
            },
        });
    };

    const handleFillBasketByWithBilling = (cardKey: TCardKey) => {
        // @ts-expect-error
        const { href } = cardLinkDataMap.get(cardKey);
        const setPrams = new URLSearchParams(href.replace('/basket/', ''));
        // @ts-ignore
        URLUpdater({ filter_tag: '' });

        mutateBilling({
            variables: {
                ...billingVars,
                payment_method: setPrams.get('payment'),
            },
        }).then(({ data }) => {
            const { billing: { promocode } } = data;
            handleFillBasketBy(cardKey, promocode || '');
        });
    };

    const openBasketPreviewAsidePanel = useBasketPreviewAside({
        openBasket,
        handleFillBasketBy: handleFillBasketByWithBilling,
        isBasketEmpty,
    });

    const handleClickLanding = (e: MouseEvent, data: ISetsItem) => {
        e.preventDefault();
        analyticService.push({
            setName: data.trackId,
            eventName: 'Track_Click_Best_Set',
        });
        const isApp = isStandaloneApp();

        if (isApp) {
            window.location.href = data.href;
            return;
        }

        window.open(data.href, '_blank');
    };

    const handleClickToSliderCard = (e: MouseEvent, cardKey: TCardKey) => {
        if (cardKey === 'HALLOWEEN_SET') {
            openDishDetails('4695', RENDERING_CONTEXT_MENU);
            return;
        }
        const data = cardLinkDataMap.get(cardKey);
        // @ts-ignore
        const isBasketUrl = data.href.includes('basket');

        if (!isBasketUrl) {
            // @ts-ignore
            handleClickLanding(e, data);
        } else {
            // @ts-ignore
            openBasketPreviewAsidePanel(e, data);
        }
    };

    /* SELECTORS */
    const isMenuEmpty = useMemo(
        () => ((selectedFilterPeriod === filterPeriod) && !eeAvailableDates) || isDisabledDateSelected,
        [selectedFilterPeriod, filterPeriod, eeAvailableDates, isDisabledDateSelected],
    );

    return (
        <div>
            <BR1258BestSetsMobileSliderV2
                isMenuEmpty={isMenuEmpty}
                cardsChain={cardsChain}
                additionalCardsChain={additionalCardsChain}
                handleClickToSliderCard={handleClickToSliderCard}
            />
        </div>
    );
});
